<script setup lang="ts">
  /**
   * Animated wave loader shown while waiting for the first AI token.
   *
   * The SVG is a sine wave masked into a circle. A CSS keyframe scrolls it
   * horizontally so it appears to dance/undulate like a snake.
   */
  import { getCurrentInstance } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed } from 'vue'

  const props = defineProps<{
    /** Optional status label displayed next to the wave (e.g. "Lese Dokumente..."). */
    label?: string
  }>()

  const { t } = useTranslation({
    en: { thinking: 'Thinking...' },
    de: { thinking: 'Denke nach...' },
    fr: { thinking: 'Je réfléchis...' }
  })

  const resolvedLabel = computed(() => props.label ?? t('thinking'))

  const maskId = `wave-mask-${getCurrentInstance()!.uid}`
</script>

<template>
  <div class="inline-flex items-center gap-4">
    <svg
      class="streaming-wave"
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        :id="maskId"
        style="mask-type: alpha"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="30"
        height="30"
      >
        <circle
          cx="14.7238"
          cy="14.7248"
          r="14.7238"
          fill="#202020"
        />
      </mask>
      <g :mask="`url(#${maskId})`">
        <path
          class="streaming-wave__path"
          d="M-2.19727 14.7297C5.128 7.39803 12.4533 7.39803 19.7785 14.7297C27.1038 22.0614 34.4291 22.0614 41.7544 14.7297C49.0796 7.39803 56.4049 7.39803 63.7302 14.7297C71.0554 22.0614 78.3807 22.0614 85.706 14.7297C93.0312 7.39803 100.357 7.39803 107.682 14.7297C115.007 22.0614 122.332 22.0614 129.658 14.7297C136.983 7.39803 144.308 7.39803 151.633 14.7297C158.959 22.0614 166.284 22.0614 173.609 14.7297C180.934 7.39803 188.26 7.39803 195.585 14.7297C202.91 22.0614 210.236 22.0614 217.561 14.7297"
          stroke="#9333EA"
          stroke-width="2"
        />
      </g>
    </svg>
    <span class="streaming-wave__label text-xs font-normal leading-4 font-inter">
      {{ resolvedLabel }}
    </span>
  </div>
</template>

<style scoped>
  .streaming-wave__path {
    animation: chat-streaming-loader-wave-scroll 1.2s linear infinite;
  }

  @keyframes chat-streaming-loader-wave-scroll {
    from {
      transform: translateX(0);
    }
    to {
      /* One full wave period ≈ 43.95px (half = 21.976). Shift left by one full period
         so the wave loops seamlessly within the circular mask. */
      transform: translateX(-43.95px);
    }
  }

  .streaming-wave__label {
    background: linear-gradient(
      90deg,
      var(--neutral-400-placeholder, #9ca3af) 0%,
      var(--neutral-300-inactive, #d1d5db) 50%,
      var(--neutral-400-placeholder, #9ca3af) 100%
    );
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
</style>
